import { useEffect } from 'react';

/**
 * Freeze background scrolling while an overlay is open.
 *
 * Without this the page behind a modal keeps scrolling under the cursor, so
 * closing the dialog leaves you somewhere you never meant to be.
 *
 * Two details this handles that a bare `overflow = 'hidden'` does not:
 *
 *  - It restores the *previous* value rather than hardcoding 'unset', so a
 *    modal opened from inside another modal cannot unlock the page when only
 *    the inner one closes.
 *  - It compensates for the vanishing scrollbar with padding, so the layout
 *    doesn't visibly jump sideways as the dialog opens.
 *
 * @param {boolean} locked - lock while true; release when false or unmounted.
 */
export default function useBodyScrollLock(locked) {
  useEffect(() => {
    if (!locked) return undefined;

    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;

    // window.innerWidth includes the scrollbar; clientWidth does not.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      const currentPad = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${currentPad + scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [locked]);
}
